import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography,
} from '@mui/material';
import { Medication } from '../../services/medicationService';
import { MeetingMedication } from '../../services/meetingMedicationService';
import {
  STOP_TREATMENT_REASON_OPTIONS, ADDITIONAL_REVIEW_REASON_OPTIONS, OTHER_OPTION,
} from '../../services/patientService';
import { MEDICATION_UNIT_OPTIONS } from '../../constants/medicationUnits';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';

// Decision slugs must match the backend validation for a titration follow-up.
export const TREATMENT_DECISION_OPTIONS: { value: string; label: string }[] = [
  { value: 'CONTINUE_CURRENT_TREATMENT', label: 'Continue Current Treatment' },
  { value: 'ADJUST_DOSE', label: 'Adjust Dose' },
  { value: 'STOP_TREATMENT', label: 'Stop Treatment' },
  { value: 'DISCHARGE_TO_GP', label: 'Discharge to GP' },
  { value: 'ADDITIONAL_REVIEW_REQUIRED', label: 'Additional Review Required' },
];

/** Only these two decisions carry reason categories. */
export const TREATMENT_DECISIONS_WITH_REASONS = ['STOP_TREATMENT', 'ADDITIONAL_REVIEW_REQUIRED'];

/** These two move the patient out of titration — confirm before writing them. */
export const TREATMENT_DECISIONS_NEEDING_CONFIRMATION = ['STOP_TREATMENT', 'DISCHARGE_TO_GP'];

/** The follow-up prescription, mirroring the meeting_medications fields it is saved to. */
export interface TitrationTreatmentMedication {
  medication_type_id: number | null;
  /** Kept as strings while editing, like the medication form in MedicationSection. */
  dosage_amount: string;
  dosage_unit: string;
  dosage_instructions: string;
  quantity: string;
}

export const EMPTY_TREATMENT_MEDICATION: TitrationTreatmentMedication = {
  medication_type_id: null,
  dosage_amount: '',
  dosage_unit: '',
  dosage_instructions: '',
  quantity: '',
};

/** Shape a saved meeting_medications row into the editable form values. */
export const toTreatmentMedication = (med: MeetingMedication): TitrationTreatmentMedication => ({
  medication_type_id: med.medication_type_id,
  dosage_amount: med.dosage_amount != null ? String(med.dosage_amount) : '',
  dosage_unit: med.dosage_unit || '',
  dosage_instructions: med.dosage_instructions || '',
  quantity: med.quantity != null ? String(med.quantity) : '',
});

export const treatmentReasonOptions = (decision: string): { value: string; label: string }[] => {
  if (decision === 'STOP_TREATMENT') return STOP_TREATMENT_REASON_OPTIONS;
  if (decision === 'ADDITIONAL_REVIEW_REQUIRED') return ADDITIONAL_REVIEW_REASON_OPTIONS;
  return [];
};

/** Which field an unmet requirement belongs to, so the message lands next to it. */
export interface TreatmentValidationError {
  field: 'reasons' | 'notes';
  message: string;
}

/**
 * The same rules the backend enforces on a titration follow-up — checked here so
 * a missing reason reads as an inline message instead of a 400 on save.
 */
export const treatmentValidationError = (
  decision: string,
  reasons: string[],
  notes: string
): TreatmentValidationError | null => {
  if (!TREATMENT_DECISIONS_WITH_REASONS.includes(decision)) return null;
  if (reasons.length === 0) {
    return { field: 'reasons', message: 'Select at least one reason.' };
  }
  // "Other" carries no clinical meaning on its own, so it has to say why.
  if (reasons.includes(OTHER_OPTION) && notes.trim().length === 0) {
    return {
      field: 'notes',
      message: decision === 'STOP_TREATMENT'
        ? 'Please explain the reason for stopping treatment.'
        : 'Please explain the reason for the additional review.',
    };
  }
  return null;
};

// Field styling without a width, so the dose pair can size its own two inputs.
const fieldClass =
  'border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none';
const inputClass = `w-full ${fieldClass}`;

interface TitrationTreatmentTabProps {
  isHebrew: boolean;
  medications: Medication[];
  decision: string;
  onDecisionChange: (value: string) => void;
  reasons: string[];
  onReasonsChange: (reasons: string[]) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  medication: TitrationTreatmentMedication;
  onMedicationChange: (patch: Partial<TitrationTreatmentMedication>) => void;
  /** Set once a save was blocked, so the unmet requirement is shown. */
  showValidationError: boolean;
  /** Opens SignatureRx — on a follow-up the medication lives here, not on the Titration tab. */
  onCreatePrescription: () => void;
  creatingPrescription: boolean;
}

/**
 * Treatment tab body for a titration follow-up meeting: the clinician's decision
 * on the titration, the prescription it leaves the patient on, and — for Stop
 * Treatment and Additional Review Required — why. The panel owns the state and
 * saves it, so this component only renders it.
 */
export const TitrationTreatmentTab: React.FC<TitrationTreatmentTabProps> = ({
  isHebrew,
  medications,
  decision,
  onDecisionChange,
  reasons,
  onReasonsChange,
  notes,
  onNotesChange,
  medication,
  onMedicationChange,
  showValidationError,
  onCreatePrescription,
  creatingPrescription,
}) => {
  const reasonOptions = treatmentReasonOptions(decision);
  const otherSelected = reasons.includes(OTHER_OPTION);
  const error = showValidationError ? treatmentValidationError(decision, reasons, notes) : null;

  const toggleReason = (value: string) => {
    onReasonsChange(
      reasons.includes(value) ? reasons.filter((r) => r !== value) : [...reasons, value]
    );
  };

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-6" dir={isHebrew ? 'rtl' : 'ltr'}>
      {/* Section 1: Treatment Decision */}
      <div>
        <h3 className="text-sm font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-1">
          Treatment Decision
        </h3>
        <div className="space-y-2">
          {TREATMENT_DECISION_OPTIONS.map(({ value, label }) => (
            <label key={value} className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="treatmentDecision"
                value={value}
                checked={decision === value}
                onChange={() => onDecisionChange(value)}
                className="text-teal-600 focus:ring-teal-500"
              />
              <span className="text-sm text-gray-700">{label}</span>
            </label>
          ))}
        </div>

        {/* Reason categories — Stop Treatment and Additional Review Required only */}
        {reasonOptions.length > 0 && (
          <div className="mt-3 space-y-3 pl-4 border-l-2 border-teal-200">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {decision === 'STOP_TREATMENT' ? 'Reason for stopping' : 'Reason for additional review'}
              </label>
              <div className="space-y-2">
                {reasonOptions.map((opt) => (
                  <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={reasons.includes(opt.value)}
                      onChange={() => toggleReason(opt.value)}
                      className="rounded text-teal-600 focus:ring-teal-500"
                    />
                    <span className="text-sm text-gray-700">{opt.label}</span>
                  </label>
                ))}
              </div>
              {error?.field === 'reasons' && (
                <p className="mt-1 text-xs text-red-600">{error.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {otherSelected ? 'Other — please specify (required)' : 'Notes (optional)'}
              </label>
              <textarea
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                dir={isHebrew ? 'rtl' : 'ltr'}
                rows={3}
                className={`${inputClass} resize-none ${error?.field === 'notes' ? 'border-red-400' : ''}`}
                placeholder={otherSelected ? 'Describe the reason...' : 'Additional notes...'}
              />
              {error?.field === 'notes' && (
                <p className="mt-1 text-xs text-red-600">{error.message}</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Section 2: Medication — prefilled from the previous titration meeting */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-3 border-b border-gray-200 pb-1">
          <h3 className="text-sm font-semibold text-gray-800">Medication</h3>
          <button
            type="button"
            onClick={onCreatePrescription}
            disabled={creatingPrescription}
            title="Create the patient in SignatureRx if needed and open SignatureRx to write the prescription"
            className="px-4 py-1 text-xs bg-teal-600 text-white rounded hover:bg-teal-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creatingPrescription ? 'Opening…' : 'Create Prescription'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Medication name</label>
            <select
              value={medication.medication_type_id ?? ''}
              onChange={(e) =>
                onMedicationChange({
                  medication_type_id: e.target.value ? Number(e.target.value) : null,
                })
              }
              className={`${inputClass} bg-white`}
            >
              <option value="">Select medication…</option>
              {medications.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dose</label>
            <div className="flex gap-2">
              <input
                type="number"
                min={0}
                step="0.01"
                value={medication.dosage_amount}
                onChange={(e) => onMedicationChange({ dosage_amount: e.target.value })}
                className={`w-24 flex-shrink-0 ${fieldClass}`}
                placeholder="e.g., 18"
              />
              <select
                value={medication.dosage_unit}
                onChange={(e) => onMedicationChange({ dosage_unit: e.target.value })}
                className={`flex-1 min-w-0 bg-white ${fieldClass}`}
              >
                <option value="">Select unit…</option>
                {MEDICATION_UNIT_OPTIONS.map((opt) => (
                  <option key={opt.id} value={opt.label}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dosage instructions</label>
            <input
              type="text"
              value={medication.dosage_instructions}
              onChange={(e) => onMedicationChange({ dosage_instructions: e.target.value })}
              className={inputClass}
              placeholder="e.g., 1 tablet in the morning"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input
              type="number"
              min={0}
              value={medication.quantity}
              onChange={(e) => onMedicationChange({ quantity: e.target.value })}
              className={inputClass}
              placeholder="e.g., 30"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const CONFIRM_TEXT: Record<string, string> = {
  STOP_TREATMENT:
    'Stop the titration treatment for this patient? The patient will be told to stop taking the medication.',
  DISCHARGE_TO_GP:
    'Discharge this patient to their GP? Their titration care moves to the GP.',
};

interface TreatmentDecisionConfirmDialogProps {
  /** The decision awaiting confirmation, or null when nothing is pending. */
  decision: string | null;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

/**
 * Confirmation for the two decisions this tab cannot take back. Deliberately a
 * dialog and not window.confirm — a native prompt freezes the whole panel.
 */
export const TreatmentDecisionConfirmDialog: React.FC<TreatmentDecisionConfirmDialogProps> = ({
  decision,
  busy,
  onCancel,
  onConfirm,
}) => {
  const label = TREATMENT_DECISION_OPTIONS.find((o) => o.value === decision)?.label || '';
  return (
    <Dialog
      open={decision !== null}
      onClose={busy ? undefined : onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: CLINIC_DIALOG_SX.paper }}
    >
      <DialogTitle sx={CLINIC_DIALOG_SX.title}>Confirm {label}</DialogTitle>
      <DialogContent sx={CLINIC_DIALOG_SX.content}>
        <Typography variant="body2" sx={CLINIC_DIALOG_SX.infoText}>
          {(decision && CONFIRM_TEXT[decision]) || ''}
        </Typography>
      </DialogContent>
      <DialogActions sx={CLINIC_DIALOG_SX.actions}>
        <Button onClick={onCancel} disabled={busy} sx={CLINIC_DIALOG_SX.cancelButton}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={busy} sx={CLINIC_DIALOG_SX.primaryButton}>
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
